import {
  Box, ButtonBase,
  Card, Chip, CircularProgress,
  Divider,
  Drawer, IconButton,
  Stack,
  Typography
} from '@mui/material';
import React, {useContext, useState} from "react";
import moment from "moment";
import _ from "lodash";
import {
  CsrCalendlyAppointmentsQuery,
  useAgentByIdLazyQuery,
  useCsrCalendlyAppointmentsLazyQuery
} from '../../../shared';
import { getUserName } from '@coverright/utils';
import { DateRange } from '../../../features';
import { X } from '@phosphor-icons/react';
import { DateRangeInput } from '@coverright/data-access/types/enrollment';

type AgentAppointment = CsrCalendlyAppointmentsQuery['csrCalendlyAppointments'][0]

const initialDateRange: DateRangeInput = {from: moment().startOf('month').toISOString(), to: moment().endOf('day').toISOString()}

interface Props {
  open: (agentId: string, dates: DateRangeInput) => void;
}

const AgentAppointmentsDrawerContext = React.createContext<Props>({
  open: () => {},
})

export function withAgentAppointmentsDrawer(WrappedComponent: React.ComponentType) {
  return () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [appointments, setAppointments] = useState<AgentAppointment[]>([]);
    const [dateRange, setDateRange] = useState(initialDateRange);
    const [agentName, setAgentName] = useState('')
    const [getAgent] = useAgentByIdLazyQuery({
      onCompleted: data => setAgentName(getUserName(data.agentById))
    });

    const [getAppointments, {loading, refetch}] = useCsrCalendlyAppointmentsLazyQuery({
      fetchPolicy: "no-cache",
      onCompleted: data => setAppointments(_.orderBy(data.csrCalendlyAppointments, 'startTime', 'desc') || []),
      notifyOnNetworkStatusChange: true,
    })

    const onClose = () => {
      setAppointments([]);
      setAgentName('');
      setDateRange(initialDateRange);
      setDrawerOpen(false);
    }

    const open = (agentId: string, dates: DateRangeInput) => {
      setAppointments([]);
      setDrawerOpen(true);
      setAgentName('');
      setDateRange(dates);
      getAppointments({
        variables: {
          agentId,
          from: moment(dates.from).format('YYYY-MM-DD'),
          to: moment(dates.to).format('YYYY-MM-DD'),
        }
      })
      getAgent({variables: {id: agentId}})
    }

    const handleDateRangeChange = (value?: DateRangeInput) => {
      setDateRange(value || initialDateRange);
      if (value) {
        refetch({
          from: moment(value.from).format('YYYY-MM-DD'),
          to: moment(value.to).format('YYYY-MM-DD'),
        })
      }
    }

    return <AgentAppointmentsDrawerContext.Provider value={{open}}>
      <AgentAppointmentsDrawer open={drawerOpen}
                               onClose={onClose}
                               loading={loading}
                               agentName={agentName}
                               dateRange={dateRange}
                               onDateRangeChange={handleDateRangeChange}
                               appointments={appointments}/>
      <WrappedComponent />
    </AgentAppointmentsDrawerContext.Provider>
  }
}

export const useAgentAppointmentsDrawer = () => {
  return useContext(AgentAppointmentsDrawerContext);
}

interface DrawerProps {
  open: boolean,
  onClose: () => void,
  appointments: AgentAppointment[],
  loading: boolean,
  dateRange: DateRangeInput,
  onDateRangeChange: (dateRange?: DateRangeInput) => void,
  agentName: string,
}

function AgentAppointmentsDrawer(props: DrawerProps) {
  const {open, onClose, appointments, dateRange, onDateRangeChange, loading, agentName} = props

  const onClick = (clientId: string) => {
    window.open(`/client/${clientId}`);
  }

  return <Drawer
    anchor={'right'}
    open={open}
    onClose={onClose}
  >
    <Card sx={{p: 4, width: 594, overflowY: 'auto'}}>
      <Box sx={{display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 3}}>
        <Typography variant={'h3'}>{agentName} appointments</Typography>
        <IconButton size={'small'} onClick={() => onClose()}>
          <X />
        </IconButton>
      </Box>

      <DateRange value={dateRange}
                 label={'Date'}
                 onChange={onDateRangeChange} />

      <Divider sx={{my: 3}} />

      {loading && <CircularProgress size={25} />}
      <Stack gap={1}>
        {appointments.map(item => (
          <ButtonBase key={item.url} onClick={() => onClick(item.invitee.clientId)}
                      sx={{border: '1px solid #e5e7eb', borderRadius: '8px', justifyContent: 'space-between', alignItems: 'flex-end', p: '8px 16px', '&:hover': {bgcolor: 'secondary.50'}}}>
            <Stack gap={.5} alignItems={'flex-start'}>
              <Typography fontWeight={500}>{item.invitee.name} with {agentName}</Typography>
              <Typography variant={'body2'} color={'text.secondary'}>
                {moment(item.startTime).format("L H:mm")}
              </Typography>
            </Stack>

            <ShowedUpChip noShow={item.noShow}/>
          </ButtonBase>
        ))}
      </Stack>
    </Card>
  </Drawer>
}

const ShowedUpChip = ({noShow}: {noShow?: boolean | null}) => {
  const label = noShow === false ? 'Showed Up' : noShow ? 'Not Showed Up' : 'No Showed Up Info';
  const color = noShow === false ? 'success' : noShow ? 'error' : 'info';
  return <Chip variant={'outlined'} size={'small'} label={label} color={color} />
}
